// askSmart-ui/src/pages/MapleCoin/operations/DashboardTab.tsx
//
// Where customer money is, its state, and whether the internal ledger is
// consistent. Every figure is derived by the server from the ledger at the
// moment of the request; nothing here calculates or stores a total.
//
// Terminology is deliberate: until a safeguarding bank is integrated, the
// backing asset is "Sandbox Funding" — never trust, safeguarded or
// reconciled funds.
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Dashboard, getDashboard } from './opsApi';
import { Amount, Busy, Loading, Panel, Unavailable, dateTime } from './shared';
import IntegrityPanel from './IntegrityPanel';
import JobsPanel from './JobsPanel';
import { isZero } from '../money';
import { OpsNav } from './MapleCoinOperations';

export default function DashboardTab({ nav }: { nav: OpsNav }) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setData(await getDashboard());
      setState('ready');
    } catch {
      setState('error');
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (state === 'loading') return <Loading label="Loading the institutional position" />;
  if (state === 'error' || !data) return <Unavailable onRetry={load} />;

  const t = data.transactions;
  return (
    <div className="mco-stack">
      <div className="mco-asof">
        <span>Position as of {dateTime(data.asOf)} · through journal #{data.lastJournalSeq ?? '—'}</span>
        {data.environment !== 'production' && <span className="mco-env">{data.environment} ledger</span>}
        <button type="button" className="mco-btn mco-btn--quiet" onClick={load} disabled={refreshing}>
          <Busy busy={refreshing}><RefreshCw size={14} aria-hidden /> Refresh</Busy>
        </button>
      </div>

      {data.currencies.map((c) => (
        <div key={c.currency} className="mco-grid">
          <Panel title={`Client funds · ${c.currency}`}>
            <p className="mco-figure" data-testid={`total-funds-${c.currency}`}>
              <Amount minor={c.totalCustomerFundsMinor} currency={c.currency} />
            </p>
            <p className="mco-figure-label">Total customer funds · {c.customerAccounts} customer account{c.customerAccounts === 1 ? '' : 's'}</p>
            <dl className="mco-kv">
              <div><dt>Available</dt><dd><Amount minor={c.availableMinor} currency={c.currency} testId={`available-${c.currency}`} /></dd></div>
              <div><dt>Pending</dt><dd><Amount minor={c.pendingMinor} currency={c.currency} testId={`pending-${c.currency}`} /></dd></div>
            </dl>
          </Panel>

          {c.sandboxFundingMinor !== null && (
            <Panel title="Sandbox funding asset">
              <p className="mco-figure" data-testid={`sandbox-${c.currency}`}>
                <Amount minor={c.sandboxFundingMinor} currency={c.currency} />
              </p>
              <p className="mco-figure-label">Sandbox funding - Development only (199910-{c.currency})</p>
              <p className="mco-note">The development and staging funding source for sandbox deposits. It is not held at a bank and exists only outside production.</p>
            </Panel>
          )}

          <Panel title="Internal balance check">
            <dl className="mco-kv">
              <div><dt>Customer liabilities</dt><dd><Amount minor={c.internalBalance.customerLiabilitiesMinor} currency={c.currency} /></dd></div>
              <div><dt>Backing sandbox asset</dt><dd><Amount minor={c.internalBalance.sandboxAssetMinor} currency={c.currency} /></dd></div>
              <div className="mco-kv-total"><dt>Difference</dt>
                <dd><Amount minor={c.internalBalance.differenceMinor} currency={c.currency} testId={`difference-${c.currency}`} /></dd></div>
            </dl>
            <p className={`mco-check ${isZero(c.internalBalance.differenceMinor) ? 'mco-check--ok' : 'mco-check--bad'}`}>
              {isZero(c.internalBalance.differenceMinor)
                ? 'Customer liabilities equal the backing asset.'
                : 'Customer liabilities and the backing asset differ. Investigate.'}
            </p>
          </Panel>
        </div>
      ))}

      <div className="mco-grid">
        <Panel title="Transactions" label="Transaction activity">
          <dl className="mco-counts">
            {([['Total', t.total], ['Completed', t.completed], ['Pending', t.pending], ['Failed', t.failed],
               ['Reversed', t.reversed], ['Reversals', t.reversals]] as [string, number][]).map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd className="mco-num" data-testid={`count-${k.toLowerCase()}`}>{v}</dd></div>
            ))}
          </dl>
          <button type="button" className="mco-link" onClick={() => nav.openTransactions()}>View all transactions</button>
        </Panel>
        <Panel title="Awaiting approval">
          <p className="mco-figure mco-num" data-testid="pending-approvals">{data.interventions.pendingApproval}</p>
          <p className="mco-figure-label">Request{data.interventions.pendingApproval === 1 ? '' : 's'} (reversals, hold and restriction releases) needing a second Operations user</p>
          <button type="button" className="mco-link" onClick={nav.openApprovals}>Review requests</button>
        </Panel>
      </div>

      <IntegrityPanel status={data.integrity}
        onChecked={(c) => setData((d) => d && { ...d, integrity: { ...d.integrity, latestCheck: c, sealsSinceCheck: '0' } })} />
      <JobsPanel />
    </div>
  );
}
